import React, { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import {
  AlertTriangle, Lock, Eye, EyeOff, Loader2, ShieldAlert, Trash2, CheckCircle2,
} from 'lucide-react';
import { toast } from './ui/sonner';

/**
 * DangerZoneSection — irreversible destructive operations, gated behind a
 * triple-confirmation flow:
 *   1. Primary Super Admin role (server-enforced)
 *   2. Ultra Password
 *   3. Type the literal phrase "RESET TEST DATA" into a confirmation field
 *
 * This is intentionally NOT a generic "wipe" button. It runs the same
 * conservative A-plan we use in dev: removes participant/test data while
 * preserving system tenant, primary admin, halls, catalogs, email templates,
 * UNFCCC whitelist, and audit logs.
 */
export default function DangerZoneSection({ tr }) {
  const [openModal, setOpen]     = useState(false);
  const [pwd, setPwd]            = useState('');
  const [showPwd, setShowPwd]    = useState(false);
  const [phrase, setPhrase]      = useState('');
  const [busy, setBusy]          = useState(false);
  const [result, setResult]      = useState(null); // success summary
  const [allowed, setAllowed]    = useState(null); // server-side primary check

  // Probe whether the current viewer is Primary Super Admin. We rely on the
  // /api/auth/me payload, which already exposes `is_main_user`.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiClient.get('/auth/me');
        if (alive) setAllowed(Boolean(r.data?.is_main_user));
      } catch {
        if (alive) setAllowed(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const phraseOk = phrase.trim().toUpperCase() === 'RESET TEST DATA';

  const submit = async () => {
    if (busy || !pwd.trim() || !phraseOk) return;
    setBusy(true);
    try {
      const r = await apiClient.post('/ultra-admin/system-settings/reset-test-data', {
        ultra_password: pwd.trim(),
        confirm_phrase: phrase.trim(),
      });
      setResult(r.data);
      toast.success(tr ? 'Test verileri sıfırlandı.' : 'Test data has been reset.');
      // Close inputs but keep modal open to show summary
      setPwd('');
      setPhrase('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    setPwd(''); setPhrase(''); setResult(null);
  };

  if (allowed === null) {
    return <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
      <Loader2 className="h-5 w-5 animate-spin" />{tr ? 'Yetki kontrol ediliyor…' : 'Checking permissions…'}
    </div>;
  }

  if (!allowed) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex gap-3">
        <ShieldAlert className="h-6 w-6 text-amber-700 flex-shrink-0" />
        <div className="text-sm text-amber-900">
          <div className="font-semibold mb-1">
            {tr ? 'Sadece Birincil Süper Admin' : 'Primary Super Admin only'}
          </div>
          <div className="text-amber-800">
            {tr
              ? 'Tehlikeli işlemler menüsüne yalnızca Birincil Süper Admin erişebilir.'
              : 'Only the Primary Super Admin can access dangerous operations.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="danger-zone">
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          {tr ? 'Tehlikeli İşlemler' : 'Dangerous Operations'}
        </h2>
        <p className="text-sm text-gray-400 mt-0.5">
          {tr
            ? 'Geri alınamaz işlemler. Yalnızca Birincil Süper Admin tarafından, üçlü onay ile çalıştırılabilir.'
            : 'Irreversible operations. Only the Primary Super Admin can run these, with triple confirmation.'}
        </p>
      </div>

      {/* Reset Test Data card */}
      <div className="border-2 border-red-200 bg-red-50/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <Trash2 className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-base">
              {tr ? 'Test Verilerini Sıfırla' : 'Reset Test Data'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {tr
                ? 'Tüm müşteri tenantları, kullanıcıları, sepetleri, siparişleri ve kayıt verilerini siler. Sistem verisi korunur.'
                : 'Deletes all customer tenants, users, carts, orders, and registration data. System data is preserved.'}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <div className="bg-white border border-red-100 rounded-xl p-3">
                <div className="text-[11px] font-bold text-red-700 mb-1.5 flex items-center gap-1">
                  <Trash2 className="h-3 w-3" />
                  {tr ? 'SİLİNECEK' : 'WILL BE DELETED'}
                </div>
                <ul className="text-xs text-gray-700 space-y-0.5 list-disc pl-4">
                  <li>{tr ? 'Tüm müşteri tenantları' : 'All customer tenants'}</li>
                  <li>{tr ? 'Tüm Birincil-olmayan Ultra Adminler' : 'All non-Primary Ultra Admins'}</li>
                  <li>{tr ? 'Sepetler, siparişler, servis siparişleri' : 'Carts, orders, service orders'}</li>
                  <li>{tr ? 'Bildirimler, onboarding, davetler' : 'Notifications, onboarding, invitations'}</li>
                  <li>{tr ? 'Bekleyen kayıtlar, doğrulama kodları' : 'Pending registrations, verification codes'}</li>
                  <li>{tr ? 'Stand rezervasyonları (standlar tekrar müsait)' : 'Stand reservations (stands become available)'}</li>
                </ul>
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-3">
                <div className="text-[11px] font-bold text-emerald-700 mb-1.5 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {tr ? 'KORUNACAK' : 'WILL BE PRESERVED'}
                </div>
                <ul className="text-xs text-gray-700 space-y-0.5 list-disc pl-4">
                  <li>{tr ? 'Birincil Süper Admin' : 'Primary Super Admin'}</li>
                  <li>{tr ? 'Sistem tenantı' : 'System tenant'}</li>
                  <li>{tr ? 'Hall layoutları + grid ayarları' : 'Halls + grid settings'}</li>
                  <li>{tr ? 'Tüm kataloglar (modüler, organizatör, tedarikçi, servis)' : 'All catalogs (modular, organizer, supplier, service)'}</li>
                  <li>{tr ? 'E-posta şablonları, sistem ayarları' : 'Email templates, system settings'}</li>
                  <li>{tr ? 'UNFCCC delege listesi (kullanıldı işareti sıfırlanır)' : 'UNFCCC delegate list (used-flags reset)'}</li>
                  <li>{tr ? 'Denetim kayıtları' : 'Audit logs'}</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setOpen(true)}
              data-testid="danger-zone-open-modal"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-sm"
            >
              <Trash2 className="h-4 w-4" />
              {tr ? 'Test Verilerini Sıfırla' : 'Reset Test Data'}
            </button>
          </div>
        </div>
      </div>

      {/* Modal — triple-confirm flow */}
      {openModal && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" data-testid="danger-zone-modal">
            <div className="px-6 py-4 border-b border-red-100 flex items-center gap-2 bg-red-50 rounded-t-2xl">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <h3 className="font-bold text-red-900">
                {result
                  ? (tr ? 'Sıfırlama Tamamlandı' : 'Reset Complete')
                  : (tr ? 'Test Verilerini Sıfırla' : 'Reset Test Data')}
              </h3>
            </div>

            {result ? (
              <div className="p-6 space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800 flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>{tr ? 'Test verileri başarıyla sıfırlandı. Aşağıda silinen kayıt sayıları yer alıyor.' : 'Test data successfully reset. Counts of deleted records below.'}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-xs space-y-1 max-h-72 overflow-y-auto">
                  {Object.entries(result.deleted_counts || {}).length === 0 && (
                    <div className="text-gray-400 italic">{tr ? 'Silinecek hiçbir kayıt yoktu.' : 'No records to delete.'}</div>
                  )}
                  {Object.entries(result.deleted_counts || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between font-mono">
                      <span className="text-gray-700">{k}</span>
                      <span className="text-red-600 font-bold">{v}</span>
                    </div>
                  ))}
                </div>
                <button onClick={close} className="w-full py-2.5 rounded-xl bg-gray-900 hover:bg-black text-white text-sm font-semibold">
                  {tr ? 'Kapat' : 'Close'}
                </button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-800 flex gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    {tr
                      ? 'Bu işlem geri ALINAMAZ. Tüm test verileri kalıcı olarak silinir. Devam etmek için aşağıdaki iki alanı doldurun.'
                      : 'This action CANNOT be undone. All test data will be permanently deleted. Fill both fields below to proceed.'}
                  </div>
                </div>

                {/* Field 1: Ultra Password */}
                <div>
                  <label className="text-xs font-semibold text-gray-700 flex items-center gap-1 mb-1.5">
                    <Lock className="h-3 w-3" /> Ultra Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      placeholder={tr ? 'Ultra şifresini girin' : 'Enter Ultra Password'}
                      data-testid="danger-zone-ultra-password"
                      className="w-full px-3 py-2 pr-9 rounded-lg border border-gray-300 text-sm focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none"
                    />
                    <button type="button" onClick={() => setShowPwd((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Field 2: Confirm phrase */}
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
                    {tr ? 'Onaylamak için yazın: ' : 'Type to confirm: '}
                    <code className="px-1.5 py-0.5 bg-gray-900 text-white rounded text-[11px] font-mono">RESET TEST DATA</code>
                  </label>
                  <input
                    type="text"
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    placeholder="RESET TEST DATA"
                    data-testid="danger-zone-confirm-phrase"
                    className={`w-full px-3 py-2 rounded-lg border text-sm font-mono outline-none focus:ring-2 ${
                      phrase && !phraseOk
                        ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                        : 'border-gray-300 focus:border-red-400 focus:ring-red-100'
                    }`}
                  />
                  {phrase && !phraseOk && (
                    <p className="text-[11px] text-red-600 mt-1">
                      {tr ? 'Aynen "RESET TEST DATA" yazmalısınız.' : 'Must match exactly: "RESET TEST DATA".'}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={close}
                    disabled={busy}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                    {tr ? 'Vazgeç' : 'Cancel'}
                  </button>
                  <button
                    onClick={submit}
                    disabled={busy || !pwd.trim() || !phraseOk}
                    data-testid="danger-zone-submit"
                    className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold flex items-center justify-center gap-1.5">
                    {busy
                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />{tr ? 'Siliniyor…' : 'Resetting…'}</>
                      : <><Trash2 className="h-3.5 w-3.5" />{tr ? 'Kalıcı Olarak Sil' : 'Permanently Delete'}</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
